import {
  Component,
  DOCUMENT,
  Inject,
  OnInit,
  PLATFORM_ID,
} from '@angular/core';
import { HeroComponent } from './hero/hero.component';
import { NavbarControlComponent } from '../shared/controls/navbar-control/navbar-control.component';
import { FooterComponent } from '../shared/controls/footer/footer.component';
import { loadComponentTranslations } from '../shared/funcs/lang-loader';
import { isPlatformBrowser } from '@angular/common';
import { Meta, Title } from '@angular/platform-browser';
import { HttpClient } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { HealthCareChallengesComponent } from './health-care-challenges/health-care-challenges.component';
import { HealthCareWorkflowComponent } from './health-care-workflow/health-care-workflow.component';
import { ContactSectionComponent } from '../shared/controls/contact-section/contact-section.component';

@Component({
  selector: 'app-health-care',
  imports: [
    HeroComponent,
    NavbarControlComponent,
    FooterComponent,
    HealthCareChallengesComponent,
    HealthCareWorkflowComponent,
    ContactSectionComponent,
  ],
  templateUrl: './health-care.component.html',
  styleUrl: './health-care.component.scss',
})
export class HealthCareComponent implements OnInit {
  public selectedLanguage: string = 'en';

  private isBrowser(): boolean {
    return isPlatformBrowser(this.platformId);
  }

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,

    private _http: HttpClient,
    private _translateService: TranslateService,
    @Inject(DOCUMENT) private document: Document,
    private metaService: Meta,
    private titleService: Title,
  ) {}

  public ngOnInit(): void {
    const pageUrl = 'https://clockflex.com/healthcare-appointment-booking';

    this.titleService.setTitle(
      'Healthcare Appointment Booking Software | ClockFlex',
    );

    this.setCanonicalUrl(pageUrl);

    this.metaService.updateTag({
      name: 'description',
      content:
        'Simplify healthcare scheduling with ClockFlex. Manage doctor appointments, clinic availability, staff schedules, and online bookings in one platform.',
    });

    // Open Graph
    this.metaService.updateTag({
      property: 'og:title',
      content: 'Healthcare Appointment Booking Software | ClockFlex',
    });

    this.metaService.updateTag({
      property: 'og:description',
      content:
        'Manage doctor appointments, clinic schedules, staff availability, and online bookings with ClockFlex.',
    });

    this.metaService.updateTag({
      property: 'og:url',
      content: pageUrl,
    });

    this.metaService.updateTag({
      property: 'og:type',
      content: 'website',
    });

    this.metaService.updateTag({
      property: 'og:image',
      content: 'https://clockflex.com/assets/images/healthcare-og-image.png',
    });

    // Twitter / X
    this.metaService.updateTag({
      name: 'twitter:card',
      content: 'summary_large_image',
    });

    this.metaService.updateTag({
      name: 'twitter:title',
      content: 'Healthcare Appointment Booking Software | ClockFlex',
    });

    this.metaService.updateTag({
      name: 'twitter:description',
      content:
        'Simplify doctor appointments and healthcare scheduling with ClockFlex.',
    });

    this.metaService.updateTag({
      name: 'twitter:image',
      content: 'https://clockflex.com/assets/images/healthcare-og-image.png',
    });
    // ✅ Language (SSR-safe default)
    this.selectedLanguage = 'en';

    if (this.isBrowser()) {
      this.selectedLanguage = localStorage.getItem('language') || 'en';
    }

    loadComponentTranslations(
      this._http,
      this._translateService,
      'components/health-care',
      this.isBrowser(),
    ).subscribe({
      next: () => {},
      error: () => {},
    });
  }

  private setCanonicalUrl(url: string): void {
    if (!url.startsWith('http')) {
      return; // safety guard
    }

    let link: HTMLLinkElement | null = this.document.querySelector(
      "link[rel='canonical']",
    );

    if (!link) {
      link = this.document.createElement('link');
      link.setAttribute('rel', 'canonical');
      this.document.head.appendChild(link);
    }

    link.setAttribute('href', url);
  }
}
